import { useState } from "react";
import { GitMerge, X, Sparkles, Loader2, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api, { errMsg } from "@/lib/api";
import { eurUnit, unitLabel, pctInt } from "@/lib/format";
import { t } from "@/i18n";

function Side({ ing, keep }) {
  return (
    <div className={`min-w-0 flex-1 rounded-lg border p-3 transition-colors ${keep ? "border-emerald-500/50 bg-emerald-500/5" : "bg-background/50"}`}>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{keep ? t("duplicatesPanel.conserver") : t("duplicatesPanel.sera_fusionne")}</p>
      <p className="mt-1 truncate font-semibold" title={ing.name}>{ing.name}</p>
      <p className="num text-xs text-muted-foreground">{eurUnit(ing.current_unit_cost)} / {unitLabel(ing.unit)}</p>
    </div>
  );
}

function Suggestion({ s, onDone }) {
  const [keepA, setKeepA] = useState(true);
  const [busy, setBusy] = useState(false);
  const keep = keepA ? s.ingredient_a : s.ingredient_b;
  const drop = keepA ? s.ingredient_b : s.ingredient_a;

  const act = async (action) => {
    setBusy(true);
    try {
      if (action === "merge") await api.post(`/duplicates/${s.id}/merge`, { keep_id: keep.id });
      else await api.post(`/duplicates/${s.id}/reject`);
      toast.success(action === "merge" ? t("duplicatesPanel.x_fusionne_dans_x", { v0: drop.name, v1: keep.name }) : t("duplicatesPanel.suggestion_rejetee"));
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border bg-card p-4 fade-up" data-testid="ai-duplicate-match-card">
      <div className="mb-3 flex items-center justify-between gap-2">
        <p className="text-sm font-semibold">{t("duplicatesPanel.correspondance_possible_detectee")}</p>
        <span className="num rounded-full bg-[hsl(var(--ai)/0.12)] px-2 py-0.5 text-[11px] font-semibold text-[hsl(var(--ai))]">{pctInt(Math.round((s.confidence || 0) * 100))}</span>
      </div>
      <div className="flex items-center gap-2">
        <Side ing={keep} keep />
        <Button variant="ghost" size="icon" className="shrink-0" onClick={() => setKeepA(!keepA)} data-testid="swap-duplicate-btn" aria-label={t("duplicatesPanel.inverser")}><ArrowLeftRight className="h-4 w-4" /></Button>
        <Side ing={drop} />
      </div>
      {s.reason && <p className="mt-3 text-xs text-muted-foreground">{s.reason}</p>}
      <div className="mt-3 flex gap-2">
        <Button size="sm" className="flex-1" disabled={busy} onClick={() => act("merge")} data-testid="merge-duplicate-btn"><GitMerge className="mr-1.5 h-3.5 w-3.5" />{t("duplicatesPanel.fusionner")}</Button>
        <Button size="sm" variant="outline" className="flex-1" disabled={busy} onClick={() => act("reject")} data-testid="reject-duplicate-btn"><X className="mr-1.5 h-3.5 w-3.5" />{t("duplicatesPanel.rejeter")}</Button>
      </div>
    </div>
  );
}

export default function DuplicatesPanel({ items, reload }) {
  const [busy, setBusy] = useState(false);

  const detect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/duplicates/detect");
      toast.success(data.found ? t("duplicatesPanel.x_doublon_s_possible_s_detecte_s", { v0: data.found }) : t("duplicatesPanel.aucun_nouveau_doublon_detecte"));
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-8 rounded-2xl border bg-gradient-to-br from-[hsl(var(--ai)/0.06)] to-transparent p-5" data-testid="duplicates-panel">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-[hsl(var(--ai)/0.12)] p-2"><Sparkles className="h-4 w-4 text-[hsl(var(--ai))]" /></div>
          <div>
            <p className="font-semibold">{t("duplicatesPanel.doublons_detectes_par_l_ia")}</p>
            <p className="text-xs text-muted-foreground">{t("duplicatesPanel.fusionnez_les_ingredients_identiques_pour")}</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={detect} disabled={busy} data-testid="detect-duplicates-btn">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}{t("duplicatesPanel.analyser_avec_l_ia")}
        </Button>
      </div>
      {items.length > 0 && (
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {items.map((s) => <Suggestion key={s.id} s={s} onDone={reload} />)}
        </div>
      )}
    </div>
  );
}
